import { z } from "zod";
import { zId } from "./common.js";

export const CUSTOM_FIELD_TYPES = [
  "text",
  "number",
  "currency",
  "date",
  "boolean",
  "select",
  "multi_select",
  "constituent_ref",
  "url",
  "file",
] as const;
export const zCustomFieldType = z.enum(CUSTOM_FIELD_TYPES);
export type CustomFieldType = z.infer<typeof zCustomFieldType>;

/** "custom_object" fields also carry objectTypeId: the org-defined type they belong to (CT-RM-34) */
export const CUSTOM_FIELD_OBJECTS = ["constituent", "gift", "pledge", "grant", "event", "custom_object"] as const;
export const zCustomFieldObject = z.enum(CUSTOM_FIELD_OBJECTS);
export type CustomFieldObject = z.infer<typeof zCustomFieldObject>;

export const zCustomFieldDef = z.object({
  id: zId,
  object: zCustomFieldObject,
  /** set when object is "custom_object" */
  objectTypeId: zId.nullable().optional(),
  key: z.string().regex(/^[a-z][a-z0-9_]*$/).max(60),
  label: z.string(),
  type: zCustomFieldType,
  groupName: z.string().nullable(),
  required: z.boolean(),
  options: z.array(z.string()).nullable().describe("for select/multi_select"),
  /** roles that may read; null = everyone */
  restrictedToPermission: z.string().nullable(),
  sortOrder: z.number().int(),
  archived: z.boolean(),
});
export type CustomFieldDef = z.infer<typeof zCustomFieldDef>;

export const zCreateCustomFieldDef = z.object({
  object: zCustomFieldObject,
  /** required when object is "custom_object" */
  objectTypeId: zId.optional(),
  key: z.string().regex(/^[a-z][a-z0-9_]*$/).max(60),
  label: z.string().trim().min(1).max(120),
  type: zCustomFieldType,
  groupName: z.string().trim().max(120).optional(),
  required: z.boolean().default(false),
  options: z.array(z.string().trim().min(1).max(120)).max(200).optional(),
  restrictedToPermission: z.string().optional(),
});

export const zUpdateCustomFieldDef = z.object({
  label: z.string().trim().min(1).max(120).optional(),
  groupName: z.string().trim().max(120).nullable().optional(),
  required: z.boolean().optional(),
  options: z.array(z.string().trim().min(1).max(120)).max(200).optional(),
  restrictedToPermission: z.string().nullable().optional(),
  sortOrder: z.number().int().optional(),
  archived: z.boolean().optional(),
});

/** Validates a custom field value against its definition. Returns an error string or null. */
export function validateCustomFieldValue(def: Pick<CustomFieldDef, "type" | "options" | "required" | "label">, value: unknown): string | null {
  if (value === null || value === undefined || value === "") {
    return def.required ? `${def.label} is required` : null;
  }
  switch (def.type) {
    case "text":
    case "url":
    case "file":
      return typeof value === "string" ? null : `${def.label} must be text`;
    case "number":
    case "currency":
      return typeof value === "number" && Number.isFinite(value) ? null : `${def.label} must be a number`;
    case "date":
      return typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value) ? null : `${def.label} must be YYYY-MM-DD`;
    case "boolean":
      return typeof value === "boolean" ? null : `${def.label} must be true/false`;
    case "select":
      return typeof value === "string" && (def.options ?? []).includes(value)
        ? null
        : `${def.label} must be one of the configured options`;
    case "multi_select":
      return Array.isArray(value) && value.every((v) => typeof v === "string" && (def.options ?? []).includes(v))
        ? null
        : `${def.label} must be a list of configured options`;
    case "constituent_ref":
      return typeof value === "string" && /^[0-9a-f-]{36}$/.test(value) ? null : `${def.label} must reference a constituent`;
    default:
      return null;
  }
}
